import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import type { Task } from '@unitly/contracts';
import { diffDays } from '../../lib/dates.ts';
import { TaskRow } from './TaskRow.tsx';
import { useOrgCtx } from '../../app/org-context.tsx';

type GroupKey = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none';
const GROUPS: { key: GroupKey; label: string }[] = [
  { key: 'overdue', label: 'In ritardo' },
  { key: 'today', label: 'Oggi' },
  { key: 'tomorrow', label: 'Domani' },
  { key: 'week', label: 'Prossimi 7 giorni' },
  { key: 'later', label: 'Più avanti' },
  { key: 'none', label: 'Senza scadenza' },
];

export function groupTasks(tasks: Task[], today: string): Map<GroupKey, Task[]> {
  const groups = new Map<GroupKey, Task[]>();
  for (const t of tasks) {
    let key: GroupKey;
    if (!t.dueDate) key = 'none';
    else if (t.isOverdue) key = 'overdue';
    else {
      const d = diffDays(t.dueDate, today);
      key = d <= 0 ? 'today' : d === 1 ? 'tomorrow' : d <= 7 ? 'week' : 'later';
    }
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  return groups;
}

export function TaskGroupList({ tasks, showWorkspace = true, workspaceId }: { tasks: Task[]; showWorkspace?: boolean; workspaceId?: string }) {
  const { today, openQuickAdd } = useOrgCtx();
  const groups = useMemo(() => groupTasks(tasks, today), [tasks, today]);
  return (
    <>
      {GROUPS.filter((g) => groups.get(g.key)?.length).map((g) => (
        <section className="section" key={g.key}>
          <div className="section-head">
            <h2 className={`section-title${g.key === 'overdue' ? ' danger' : ''}`}>
              {g.label} <span className="n">{groups.get(g.key)!.length}</span>
            </h2>
            {g.key === 'today' && (
              <button type="button" className="btn btn-plain btn-sm" onClick={() => openQuickAdd({ dueDate: today, workspaceId })}>
                <Plus size={16} /> Aggiungi
              </button>
            )}
          </div>
          <div className="card list">
            {groups.get(g.key)!.map((t) => (
              <TaskRow key={t.id} task={t} showWorkspace={showWorkspace} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

export function DoneList({ tasks, showWorkspace = true }: { tasks: Task[]; showWorkspace?: boolean }) {
  const [open, setOpen] = useState(false);
  if (!tasks.length) return null;
  return (
    <section className="section">
      <button type="button" className="section-head" style={{ width: '100%' }} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <h2 className="section-title" style={{ color: 'var(--label-2)' }}>
          Completate <span className="n">{tasks.length}</span> {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </h2>
      </button>
      {open && (
        <div className="card list">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} showWorkspace={showWorkspace} />
          ))}
        </div>
      )}
    </section>
  );
}
